/**
 * Central Kitchen API — Requisitions & Daily Prep
 * Base: https://x1f3lmpgxa.execute-api.ap-south-1.amazonaws.com/Stage
 * Auth: Cognito ID token (Bearer)
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL =
  'https://api.innoworx.ai/Stage';

const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens?.idToken) {
      return tokens.idToken.toString();
    }
    return null;
  } catch (error) {
    console.error('Central Kitchen API: Error fetching auth session:', error);
    return null;
  }
};

const handleResponse = async (response, endpoint = '', method = 'GET') => {
  const text = await response.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = {};
  }
  if (!response.ok) {
    const message = data?.error || data?.message || `HTTP ${response.status}`;
    const error = new Error(message);
    error.status = response.status;
    error.statusText = response.statusText;
    error.endpoint = endpoint;
    error.responseData = data;
    
    // Log API error to logging service
    loggingService.logError(
      'api_error_central_kitchen',
      `Central Kitchen API error: ${response.status} ${response.statusText} for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        status: response.status,
        statusText: response.statusText,
        responseData: data,
      }
    ).catch(logError => {
      console.warn('[centralKitchenApi] Failed to log error:', logError);
    });
    
    throw error;
  }
  return data;
};

/**
 * GET /central-kitchen/requisitions
 * @param {{ outlet_id?: string, transfer_status?: string }} params
 * @returns {Promise<{ requisitions: Array }>}
 */
export const listRequisitions = async (params = {}) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication required. Please log in.');
  }
  const q = new URLSearchParams();
  if (params.outlet_id) q.set('outlet_id', params.outlet_id);
  if (params.transfer_status) q.set('transfer_status', params.transfer_status);
    const query = q.toString();
    const url = `${API_BASE_URL}/central-kitchen/requisitions${query ? `?${query}` : ''}`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await handleResponse(response, url, 'GET');
      
      // Log API event for successful fetch
      loggingService.logInfo(
        'api_success_central_kitchen',
        `Successfully fetched requisitions`,
        {
          endpoint: '/central-kitchen/requisitions',
          method: 'GET',
          outletId: params.outlet_id,
          transferStatus: params.transfer_status,
        }
      ).catch(logError => {
        console.warn('[centralKitchenApi] Failed to log success event:', logError);
      });
      
      return data;
  } catch (error) {
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_central_kitchen',
      `Central Kitchen API network/fetch error for /central-kitchen/requisitions`,
      error,
      {
        endpoint: '/central-kitchen/requisitions',
        method: 'GET',
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[centralKitchenApi] Failed to log error:', logError);
    });
    throw error;
  }
};

/**
 * POST /central-kitchen/requisitions
 * @param {{ outlet_id?: string, site_id?: string, needed_by_date: string, line_items: Array, transfer_status?: string }} body
 * @returns {Promise<{ message: string, requisition_id: string }>}
 */
export const createRequisition = async (body) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication required. Please log in.');
  }
  const payload = {
    outlet_id: body.outlet_id || body.site_id,
    site_id: body.site_id || body.outlet_id,
    needed_by_date: body.needed_by_date,
    line_items: (body.line_items || body.items || []).map((li) => ({
      item_id: li.item_id,
      requested_quantity: Number(li.requested_quantity ?? li.quantity ?? 0),
      unit: li.unit || '',
      notes: li.notes || '',
    })),
  };
  if (body.transfer_status) payload.transfer_status = body.transfer_status;

  try {
    const response = await fetch(`${API_BASE_URL}/central-kitchen/requisitions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    const data = await handleResponse(response, '/central-kitchen/requisitions', 'POST');
    
    // Log API event for creating requisition
    loggingService.logInfo(
      'api_success_central_kitchen',
      `Successfully created requisition`,
      {
        endpoint: '/central-kitchen/requisitions',
        method: 'POST',
        outletId: payload.outlet_id,
        siteId: payload.site_id,
        lineItemsCount: payload.line_items?.length || 0,
      }
    ).catch(logError => {
      console.warn('[centralKitchenApi] Failed to log success event:', logError);
    });
    
    return data;
  } catch (error) {
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_central_kitchen',
      `Central Kitchen API network/fetch error for /central-kitchen/requisitions`,
      error,
      {
        endpoint: '/central-kitchen/requisitions',
        method: 'POST',
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[centralKitchenApi] Failed to log error:', logError);
    });
    throw error;
  }
};

/**
 * GET /central-kitchen/requisitions/{requisition_id}
 */
export const getRequisition = async (requisitionId) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication required. Please log in.');
  }
  try {
    const endpoint = `/central-kitchen/requisitions/${encodeURIComponent(requisitionId)}`;
    const response = await fetch(
      `${API_BASE_URL}${endpoint}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const data = await handleResponse(response, endpoint, 'GET');
    
    // Log API event for getting requisition
    loggingService.logInfo(
      'api_success_central_kitchen',
      `Successfully fetched requisition ${requisitionId}`,
      {
        endpoint: `/central-kitchen/requisitions/${requisitionId}`,
        method: 'GET',
        requisitionId,
      }
    ).catch(logError => {
      console.warn('[centralKitchenApi] Failed to log success event:', logError);
    });
    
    return data;
  } catch (error) {
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_central_kitchen',
      `Central Kitchen API network/fetch error for /central-kitchen/requisitions/${requisitionId}`,
      error,
      {
        endpoint: `/central-kitchen/requisitions/${requisitionId}`,
        method: 'GET',
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[centralKitchenApi] Failed to log error:', logError);
    });
    throw error;
  }
};

/**
 * PUT /central-kitchen/requisitions/{requisition_id}
 * Only updates transfer_status and/or delivery_vehicle_id.
 * @param {string} requisitionId
 * @param {{ transfer_status?: string, delivery_vehicle_id?: string }} body
 */
export const updateRequisition = async (requisitionId, body) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication required. Please log in.');
  }
  const payload = {};
  if (body.transfer_status != null) payload.transfer_status = body.transfer_status;
  if (body.delivery_vehicle_id != null)
    payload.delivery_vehicle_id = body.delivery_vehicle_id;
  if (Object.keys(payload).length === 0) {
    throw new Error('No updatable fields provided.');
  }

  try {
    const endpoint = `/central-kitchen/requisitions/${encodeURIComponent(requisitionId)}`;
    const response = await fetch(
      `${API_BASE_URL}${endpoint}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      }
    );
    const data = await handleResponse(response, endpoint, 'PUT');
    
    // Log API event for updating requisition
    loggingService.logInfo(
      'api_success_central_kitchen',
      `Successfully updated requisition ${requisitionId}`,
      {
        endpoint: `/central-kitchen/requisitions/${requisitionId}`,
        method: 'PUT',
        requisitionId,
        updatedFields: Object.keys(payload),
      }
    ).catch(logError => {
      console.warn('[centralKitchenApi] Failed to log success event:', logError);
    });
    
    return data;
  } catch (error) {
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_central_kitchen',
      `Central Kitchen API network/fetch error for /central-kitchen/requisitions/${requisitionId}`,
      error,
      {
        endpoint: `/central-kitchen/requisitions/${requisitionId}`,
        method: 'PUT',
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[centralKitchenApi] Failed to log error:', logError);
    });
    throw error;
  }
};

/**
 * DELETE /central-kitchen/requisitions/{requisition_id}
 */
export const deleteRequisition = async (requisitionId) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication required. Please log in.');
  }
  try {
    const endpoint = `/central-kitchen/requisitions/${encodeURIComponent(requisitionId)}`;
    const response = await fetch(
      `${API_BASE_URL}${endpoint}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const data = await handleResponse(response, endpoint, 'DELETE');
    
    // Log API event for deleting requisition
    loggingService.logInfo(
      'api_success_central_kitchen',
      `Successfully deleted requisition ${requisitionId}`,
      {
        endpoint: `/central-kitchen/requisitions/${requisitionId}`,
        method: 'DELETE',
        requisitionId,
      }
    ).catch(logError => {
      console.warn('[centralKitchenApi] Failed to log success event:', logError);
    });
    
    return data;
  } catch (error) {
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_central_kitchen',
      `Central Kitchen API network/fetch error for /central-kitchen/requisitions/${requisitionId}`,
      error,
      {
        endpoint: `/central-kitchen/requisitions/${requisitionId}`,
        method: 'DELETE',
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[centralKitchenApi] Failed to log error:', logError);
    });
    throw error;
  }
};

/**
 * GET /driver/vehicle/getall-by-sites
 * Returns vehicles for a given site (base_location).
 * @param {string} siteId - Required. Site ID (base_location).
 * @returns {Promise<Array<{ vehicle_id: string, model_name?: string, registration_no?: string, ... }>>}
 */
export const getVehiclesBySiteId = async (siteId) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication required. Please log in.');
  }
  if (!siteId) {
    throw new Error('site_id is required.');
  }
  const q = new URLSearchParams({ site_id: siteId });
  const url = `${API_BASE_URL}/driver/vehicle/getall-by-sites?${q.toString()}`;
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await handleResponse(response, url, 'GET');
    const vehicles = Array.isArray(data) ? data : (data?.vehicles ?? []);
    
    // Log API event for getting vehicles
    loggingService.logInfo(
      'api_success_central_kitchen',
      `Successfully fetched vehicles for site ${siteId}`,
      {
        endpoint: '/driver/vehicle/getall-by-sites',
        method: 'GET',
        siteId,
        vehiclesCount: vehicles.length,
      }
    ).catch(logError => {
      console.warn('[centralKitchenApi] Failed to log success event:', logError);
    });
    
    return vehicles;
  } catch (error) {
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_central_kitchen',
      `Central Kitchen API network/fetch error for /driver/vehicle/getall-by-sites`,
      error,
      {
        endpoint: '/driver/vehicle/getall-by-sites',
        method: 'GET',
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[centralKitchenApi] Failed to log error:', logError);
    });
    throw error;
  }
};

/**
 * GET /stock/items/{stock_item_id}
 * Fetches a single stock item by ID.
 * @param {string} stockItemId
 * @returns {Promise<{ item: { stock_item_id, item_name, unit_of_measure, ... } }>}
 */
export const getStockItem = async (stockItemId) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication required. Please log in.');
  }
  try {
    const endpoint = `/stock/items/${encodeURIComponent(stockItemId)}`;
    const response = await fetch(
      `${API_BASE_URL}${endpoint}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const data = await handleResponse(response, endpoint, 'GET');
    
    // Log API event for getting stock item
    loggingService.logInfo(
      'api_success_central_kitchen',
      `Successfully fetched stock item ${stockItemId}`,
      {
        endpoint: `/stock/items/${stockItemId}`,
        method: 'GET',
        stockItemId,
      }
    ).catch(logError => {
      console.warn('[centralKitchenApi] Failed to log success event:', logError);
    });
    
    return data;
  } catch (error) {
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_central_kitchen',
      `Central Kitchen API network/fetch error for /stock/items/${stockItemId}`,
      error,
      {
        endpoint: `/stock/items/${stockItemId}`,
        method: 'GET',
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[centralKitchenApi] Failed to log error:', logError);
    });
    throw error;
  }
};

/**
 * GET /stock/items
 * Fetches stock items for line item selection (e.g. Create requisition).
 * @param {{ site_id?: string, limit?: number }} [params]
 * @returns {Promise<{ items: Array }>} items have stock_item_id/item_id, item_name/name, unit/unit_of_measure
 */
export const getStockItems = async (params = {}) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication required. Please log in.');
  }
  const q = new URLSearchParams();
  if (params.site_id) q.set('site_id', params.site_id);
  if (params.limit) q.set('limit', String(params.limit));
  const query = q.toString();
  const url = `${API_BASE_URL}/stock/items${query ? `?${query}` : ''}`;
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await handleResponse(response, url, 'GET');
    const items = data?.items ?? [];
    
    // Log API event for getting stock items
    loggingService.logInfo(
      'api_success_central_kitchen',
      `Successfully fetched stock items`,
      {
        endpoint: '/stock/items',
        method: 'GET',
        siteId: params.site_id,
        limit: params.limit,
        itemsCount: items.length,
      }
    ).catch(logError => {
      console.warn('[centralKitchenApi] Failed to log success event:', logError);
    });
    
    return { items };
  } catch (error) {
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_central_kitchen',
      `Central Kitchen API network/fetch error for /stock/items`,
      error,
      {
        endpoint: '/stock/items',
        method: 'GET',
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[centralKitchenApi] Failed to log error:', logError);
    });
    throw error;
  }
};

/**
 * GET /central-kitchen/daily-prep?prep_date=YYYY-MM-DD
 * @param {string} [prepDate] - YYYY-MM-DD, default today
 */
export const getDailyPrep = async (prepDate) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication required. Please log in.');
  }
  const q = new URLSearchParams();
  if (prepDate) q.set('prep_date', prepDate);
  const query = q.toString();
  const url = `${API_BASE_URL}/central-kitchen/daily-prep${query ? `?${query}` : ''}`;
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await handleResponse(response, url, 'GET');
    
    // Log API event for getting daily prep
    loggingService.logInfo(
      'api_success_central_kitchen',
      `Successfully fetched daily prep`,
      {
        endpoint: '/central-kitchen/daily-prep',
        method: 'GET',
        prepDate,
      }
    ).catch(logError => {
      console.warn('[centralKitchenApi] Failed to log success event:', logError);
    });
    
    return data;
  } catch (error) {
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_central_kitchen',
      `Central Kitchen API network/fetch error for /central-kitchen/daily-prep`,
      error,
      {
        endpoint: '/central-kitchen/daily-prep',
        method: 'GET',
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[centralKitchenApi] Failed to log error:', logError);
    });
    throw error;
  }
};
